'use client';

// React error boundary - turns a component crash into a contained, recoverable
// panel instead of a blank white screen. Today a single missing import
// (`btnBase`) killed the entire app; with this, only the offending view shows a
// fallback and the rest keeps working. Crashes are reported to /api/errors.
//
// Chunk-load / stale-deploy crashes auto-recover: a deploy swaps chunk hashes,
// so an open tab on the old build throws on lazy code; one reload fixes it. We
// reload at most once per build (lib/chunk-reload.mjs), so a genuine bug can't
// loop the page and lands on this fallback after the one reload. This module is
// in app/layout.js, so it also installs the window listeners for import()
// failures outside render.

import React from 'react';
import { reportError } from '@/components/ErrorReporter';
import { reloadForChunkError, clearChunkReload, installChunkReloadListeners } from '@/lib/chunk-reload.mjs';

if (typeof window !== 'undefined') installChunkReloadListeners(window);

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    try {
      reportError({
        kind: 'boundary',
        message: `[${this.props.label || 'app'}] ${error?.message || error}`,
        stack: (error?.stack || '') + '\n--- componentStack ---' + (info?.componentStack || ''),
      });
    } catch { /* never let reporting throw */ }

    // Stale-deploy chunk error → reload once to pick up the new build.
    reloadForChunkError(error);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    // A bare fallback that depends on NOTHING from the app (no design tokens,
    // no imports that might themselves be the thing that broke).
    const wrap = {
      padding: '28px 24px', maxWidth: 520, margin: '40px auto', textAlign: 'center',
      fontFamily: 'system-ui, -apple-system, sans-serif', color: '#1a1a1a',
    };
    const btn = {
      padding: '8px 16px', fontSize: 13 /* = HV2.fsText */, fontWeight: 600, fontFamily: 'inherit',
      // Universele knop-diepte, waarden = BTN_RELIEF in components/HomeV2/lib.js
      // (bewust niet geimporteerd: deze boundary mag nergens van afhangen).
      borderRadius: 5, cursor: 'pointer', border: '1px solid #1a1a1a', background: '#fff', color: '#1a1a1a',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 1px rgba(0,0,0,0.22)',
    };
    return (
      <div style={wrap} role="alert">
        <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="#c0432a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 10 }}>
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <div style={{ fontSize: 13 /* = HV2.fsText */, fontWeight: 700, marginBottom: 6 }}>Er ging iets mis</div>
        <div style={{ fontSize: 13 /* = HV2.fsText */, color: '#666', marginBottom: 16, lineHeight: 1.5 }}>
          {this.props.label ? `Dit onderdeel (${this.props.label}) kon niet worden geladen.` : 'Dit onderdeel kon niet worden geladen.'} De rest van de app werkt nog. Probeer het opnieuw of vernieuw de pagina.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button onClick={this.reset} style={btn}>Opnieuw proberen</button>
          <button onClick={() => { clearChunkReload(); window.location.reload(); }} style={{ ...btn, background: '#2d4f9e' /* BTN_T.accent */, color: '#fff' }}>Vernieuw pagina</button>
        </div>
      </div>
    );
  }
}
